// Acceptance table (IMPLEMENTATION §2.1 rules/accept, §0.2 R1/R4–R9, GDD §4): may building `b` take one
// `fam` T`tier` item right now, entering from side `side` (null = dropped by hand), counting the items
// already in transit to it (`pending`, R8 reservations)? Returns null when it may, else the reject reason
// the drop preview shows ("{machine} takes {fam}" = notAccepted, "Queue full" = full, "Cell taken").
//   processors / Packer  take inputs on the 3 non-front sides; sinks (Crate, Gallery, Dock) on all 4;
//   a chute only from its back, and only when its chain sink accepts the item now (smart pull, R8);
//   ghosts and paused buildings are never fed automatically (hand delivery to a ghost is `deliver`, not
//   an insert); deposits and drills take nothing.

import { recipeOf, slotCap, slotFams } from '../bdefs.ts';
import type { RejectReason } from '../commands.ts';
import { cellIndex, stepCell } from '../core/grid.ts';
import type { Dir, Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { famDef, findBuilding } from '../entities.ts';
import type { FamId } from '../gen/ids.ts';
import { isFree, occAt } from '../occupancy.ts';
import type { Building, ChuteB, CrateB, ItemRef, ProcessorB } from '../state.ts';
import { reservedAt, topoOf } from '../topology.ts';

/** Items in a Crate. */
export function crateTotal(b: CrateB): Int {
  let n = 0;
  for (const k of b.counts) n += k;
  return n;
}

function countFam(xs: readonly ItemRef[], fam: FamId): Int {
  let n = 0;
  for (const r of xs) if (r.fam === fam) n++;
  return n;
}

function otherFam(xs: readonly ItemRef[], fam: FamId): boolean {
  for (const r of xs) if (r.fam !== fam) return true;
  return false;
}

/** Heat the pending fuel items will add (R6). */
function pendingHeat(c: Ctx, xs: readonly ItemRef[], fuel: FamId): Int {
  const heat = famDef(c.d, fuel).fuelHeat;
  let h = 0;
  for (const r of xs) if (r.fam === fuel) h += heat[r.tier - 1] ?? 0;
  return h;
}

function processorAccepts(
  c: Ctx,
  b: ProcessorB,
  fam: FamId,
  tier: Int,
  pending: readonly ItemRef[],
): RejectReason | null {
  if (b.type === 'smelter') {
    const sm = c.d.buildings.smelter;
    if (fam === sm.fuel) {
      const h = famDef(c.d, fam).fuelHeat[tier - 1];
      if (h === undefined) return 'notAccepted';
      const cap = sm.heatCap[b.level - 1] ?? 0;
      return b.heat + pendingHeat(c, pending, fam) + h <= cap ? null : 'full';
    }
  }
  const fams = slotFams(c.d, b);
  for (let i = 0; i < fams.length; i++) {
    if (fams[i] !== fam) continue;
    if (tier > recipeOf(c.d, b).maxTier) return 'notAccepted';
    const inSlot = (b.slots[i] ?? []).length;
    return inSlot + countFam(pending, fam) < slotCap(c.d, b.type) ? null : 'full';
  }
  return 'notAccepted';
}

/** Why the chain sink of chute `ch` refuses the item now, or null (R8 smart pull / push check). */
export function chainRefuses(
  c: Ctx,
  ch: ChuteB,
  fam: FamId,
  tier: Int,
  exclude: Int = 0,
): RejectReason | null {
  const topo = topoOf(c);
  const ci = cellIndex(c.occ.cols, ch.x, ch.y);
  const sink = topo.sink[ci] ?? -1;
  if (sink < 0) return 'noRoom';
  const sx = sink % c.occ.cols;
  const sy = (sink - sx) / c.occ.cols;
  const all = topo.reserved[sink] ?? [];
  const pending = exclude === 0 ? all : all.filter((r) => r.id !== exclude);
  const id = occAt(c.occ, sx, sy);
  const target = id > 0 ? findBuilding(c.s, id) : null;
  if (target === null) {
    // floor sink: the tile must be empty and nothing else on its way there
    return isFree(c.occ, sx, sy) && pending.length === 0 ? null : 'noRoom';
  }
  return acceptReason(c, target, fam, tier, topo.sinkSide[ci] ?? null, pending);
}

/**
 * Why `b` refuses one `fam` T`tier` item now, or null. `side` = the side of `b` it arrives on (null = by
 * hand: no side rule, and a paused machine still takes it); `pending` = items already on their way to `b`.
 */
export function acceptReason(
  c: Ctx,
  b: Building,
  fam: FamId,
  tier: Int,
  side: Dir | null,
  pending: readonly ItemRef[],
): RejectReason | null {
  if (b.type === 'deposit' || b.type === 'drill') return 'occupied';
  if (b.type === 'chute') {
    if (side === null) return 'occupied';
    if (side !== b.inDir) return 'notAccepted';
    if (b.carry !== null) return 'full';
    return chainRefuses(c, b, fam, tier);
  }
  if (b.ghost !== null) return 'notAccepted';
  if (b.paused && side !== null) return 'notAccepted';
  const kind = famDef(c.d, fam).kind;
  switch (b.type) {
    case 'crate': {
      if (kind === 'gun') return 'notAccepted';
      const f = b.fam ?? (pending.length > 0 ? (pending[0] as ItemRef).fam : fam);
      if (f !== fam || otherFam(pending, fam)) return 'notAccepted';
      const cap = c.d.buildings.crate.capacity[b.level - 1] ?? 0;
      return crateTotal(b) + pending.length < cap ? null : 'full';
    }
    case 'gallery': {
      if (fam !== 'ammo') return 'notAccepted';
      return b.queue.length + pending.length < c.d.buildings.gallery.ammoQueue ? null : 'full';
    }
    case 'packer': {
      if (side !== null && side === b.dir) return 'notAccepted';
      if (kind === 'gun') return 'notAccepted';
      const f = b.lockFam ?? (pending.length > 0 ? (pending[0] as ItemRef).fam : fam);
      if (f !== fam || otherFam(pending, fam)) return 'notAccepted';
      return b.buf.length + pending.length < c.d.buildings.packer.buffer ? null : 'full';
    }
    case 'dock':
      return 'notAccepted'; // v0.2
    default:
      if (side !== null && side === b.dir) return 'notAccepted';
      return processorAccepts(c, b, fam, tier, pending);
  }
}

/** Automatic insert check (push, deposit, chute): counts the items in transit to `b`. */
export function acceptsAuto(c: Ctx, b: Building, fam: FamId, tier: Int, side: Dir): boolean {
  return acceptReason(c, b, fam, tier, side, reservedAt(c, b.x, b.y)) === null;
}

/** R1 automatic push from a deposit on side `from` of `b` (ghosts never; a chute only from its back). */
export function acceptsFromDeposit(c: Ctx, b: Building, fam: FamId, tier: Int, from: Dir): boolean {
  return acceptsAuto(c, b, fam, tier, from);
}

/** The side of the building at (bx, by) facing its neighbour (ax, ay): where an item from there enters. */
export function entrySide(bx: Int, by: Int, ax: Int, ay: Int): Dir {
  if (ax > bx) return 'E';
  if (ax < bx) return 'W';
  return ay > by ? 'S' : 'N';
}

/** The cell a building pushes into (its front). */
export function frontOf(b: Building): { x: Int; y: Int } {
  return stepCell(b.x, b.y, b.dir);
}
